import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import api from '../services/api';
import { 
  MessageSquare, 
  Star, 
  ThumbsUp, 
  ThumbsDown, 
  Send,
  CheckCircle,
  AlertCircle,
  Lightbulb,
  Zap,
  Target,
  Users,
  Award
} from 'lucide-react';

const Feedback = () => {
  const { user, isAuthenticated } = useUser();
  const [activeTab, setActiveTab] = useState('feedback');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [category, setCategory] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const feedbackCategories = [
    { id: 'general', name: 'General Feedback', icon: MessageSquare },
    { id: 'ai-performance', name: 'AI Performance', icon: Zap },
    { id: 'user-interface', name: 'User Interface', icon: Target },
    { id: 'features', name: 'Features & Functionality', icon: Award },
    { id: 'bugs', name: 'Bug Reports', icon: AlertCircle },
    { id: 'suggestions', name: 'Feature Suggestions', icon: Lightbulb }
  ];

  const handleRatingClick = (value) => {
    setRating(value);
  };

  const handleRatingHover = (value) => {
    setHoveredRating(value);
  };

  const handleRatingLeave = () => {
    setHoveredRating(0);
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      setError('Please log in to submit feedback');
      return;
    }

    if (!feedbackText.trim() || !category) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await api.submitFeedback({
        category,
        rating,
        message: feedbackText.trim()
      });

      if (response.success) {
        setSubmitted(true);
        setFeedbackText('');
        setCategory('');
        setRating(0);
        setTimeout(() => {
          setSubmitted(false);
        }, 3000);
      } else {
        setError(response.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = index + 1;
      const isFilled = starValue <= (hoveredRating || rating);
      
      return (
        <button
          key={starValue}
          onClick={() => handleRatingClick(starValue)}
          onMouseEnter={() => handleRatingHover(starValue)}
          onMouseLeave={handleRatingLeave}
          className={`text-3xl transition-colors ${
            isFilled ? 'text-yellow-400' : 'text-gray-300'
          } hover:text-yellow-400`}
        >
          ★
        </button>
      );
    });
  };

  const getRatingText = () => {
    if (rating === 0) return 'Select a rating';
    const ratings = {
      1: 'Poor',
      2: 'Fair',
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent'
    };
    return ratings[rating];
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Feedback & Support</h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Help us improve DebateRoyale by sharing your thoughts and reporting issues
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
            <button
              onClick={() => setActiveTab('feedback')}
              className={`px-6 py-3 rounded-md font-medium transition-colors ${
                activeTab === 'feedback'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Feedback
            </button>
            <button
              onClick={() => setActiveTab('support')}
              className={`px-6 py-3 rounded-md font-medium transition-colors ${
                activeTab === 'support'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Support
            </button>
          </div>
        </motion.div>

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {/* Rating Section */}
            <div className="card">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <Star className="w-6 h-6 mr-2 text-yellow-500" />
                Rate Your Experience
              </h2>
              
              <div className="text-center mb-6">
                <div className="flex justify-center space-x-2 mb-4">
                  {renderStars()}
                </div>
                <p className="text-lg font-medium text-gray-700 dark:text-gray-200">{getRatingText()}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <ThumbsUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold text-green-800 dark:text-green-200 mb-1">What's Working Well?</h3>
                  <p className="text-sm text-green-700 dark:text-green-300">Tell us what you love about DebateRoyale</p>
                </div>
                <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <ThumbsDown className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <h3 className="font-semibold text-red-800 dark:text-red-200 mb-1">What Can We Improve?</h3>
                  <p className="text-sm text-red-700 dark:text-red-300">Help us identify areas for enhancement</p>
                </div>
              </div>
            </div>

            {/* Feedback Form */}
            <div className="card">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <MessageSquare className="w-6 h-6 mr-2" />
                Share Your Feedback
              </h2>

              <div className="space-y-6">
                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Feedback Category
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {feedbackCategories.map((cat) => {
                      const Icon = cat.icon;
                      return (
                        <label
                          key={cat.id}
                          className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                            category === cat.id
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <input
                            type="radio"
                            name="category"
                            value={cat.id}
                            checked={category === cat.id}
                            onChange={(e) => setCategory(e.target.value)}
                            className="sr-only"
                          />
                          <Icon className="w-5 h-5 mr-3 text-gray-600 dark:text-gray-400" />
                          <span className="font-medium text-gray-900 dark:text-white">{cat.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Feedback Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Feedback
                  </label>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Tell us about your experience, suggestions, or any issues you've encountered..."
                    rows={6}
                    className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
                  >
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                      <span className="text-red-700 dark:text-red-400">{error}</span>
                    </div>
                  </motion.div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSubmit}
                    disabled={!feedbackText.trim() || !category || submitted || submitting}
                    className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitted ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Thank You!
                      </>
                    ) : submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Feedback
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Success Message */}
            {submitted && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="card bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
              >
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                  <div>
                    <h3 className="font-semibold text-green-800 dark:text-green-200">Feedback Submitted!</h3>
                    <p className="text-green-700 dark:text-green-300">Thank you for helping us improve DebateRoyale.</p>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {/* FAQ Section */}
            <div className="card">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <MessageSquare className="w-6 h-6 mr-2" />
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-4">
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">How do I start my first debate?</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Navigate to the "Live Debate" section, choose your topic and format, then click "Start Debate" to begin your session.
                  </p>
                </div>
                
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">How does the XP system work?</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Earn XP by completing training modules, winning debates, and participating in tournaments. Level up to unlock new features and achievements.
                  </p>
                </div>
                
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Can I practice with friends?</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Yes! You can invite friends to private debate sessions or join public debates with other users from around the world.
                  </p>
                </div>
                
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">How do tournaments work?</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Tournaments are competitive events where you can compete against other debaters for prizes, trophies, and recognition on the leaderboard.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Support */}
            <div className="card">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <MessageSquare className="w-6 h-6 mr-2" />
                Contact Support
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="text-center p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <MessageSquare className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Email Support</h3>
                  <p className="text-blue-700 dark:text-blue-300 mb-3">Get help via email within 24 hours</p>
                  <a href="mailto:support@debateroyale.com" className="btn-primary">
                    Send Email
                  </a>
                </div>
                
                <div className="text-center p-6 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <Users className="w-8 h-8 text-green-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">Community Forum</h3>
                  <p className="text-green-700 dark:text-green-300 mb-3">Connect with other debaters</p>
                  <button className="btn-primary">
                    Join Forum
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Feedback; 